import { MydraLogo } from '@/app/icons/MydraLogo'
import { EXTERNAL_LINKS, WEBSITE_LINKS } from '@/app/lib/constants'
import { LanguageSelector } from '@/app/modules/shared/components/language-selector'
import { useLanguageSelection } from '@/app/modules/shared/providers/LanguageSelectionProvider'
import { CircularProgress } from '@mui/material'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'

type FooterProps = React.ComponentProps<'footer'>

export function Footer({ ...props }: FooterProps) {
  const t = useTranslations('Footer')
  const { isLoading: isLanguageLoading } = useLanguageSelection()

  return (
    <footer
      {...props}
      className={`mt-auto bg-mydra-black ${props.className ?? ''}`}
    >
      <div className="container flex flex-col gap-4 overflow-x-hidden pb-5 pt-8 text-sm text-white md:grid md:grid-cols-12 md:gap-x-8 md:gap-y-0 md:pt-16">
        <MydraLogo
          className="h-9 self-start text-white md:col-span-4"
          showByStudentFinance
        />

        <nav className="hidden text-base font-medium md:col-span-4 md:col-start-9 md:row-span-4 md:row-start-3 md:flex md:items-end">
          <ul className="mt-6 flex w-full items-end justify-end gap-8">
            <li>
              <Link target="_blank" href={WEBSITE_LINKS.help}>
                {t('help')}
              </Link>
            </li>
            <li>
              <Link target="_blank" href={WEBSITE_LINKS.faqs}>
                {t('faqs')}
              </Link>
            </li>
            <li>
              <Link target="_blank" href={WEBSITE_LINKS.paymentOptions}>
                {t('payment_options')}
              </Link>
            </li>
          </ul>
        </nav>
        <address className="row-start-2 not-italic md:col-span-4 md:col-start-1 md:mt-8">
          <p className="leading-[150%]">{t('address')}</p>
        </address>
        <p className="text-xs leading-[160%] md:col-span-4 md:col-start-1 md:mt-9">
          {t('registration_info')}
        </p>

        <div className="flex flex-col md:col-span-full md:col-start-1 md:mt-6 md:flex-row md:items-center md:gap-4">
          <div className="flex items-center">
            <Image
              src="/images/eif-logo.png"
              width={145}
              height={50}
              alt="EIF logo"
              className="h-12 w-auto"
            />
            <Image
              src="/images/eif-classic-logo.png"
              width={227}
              height={64}
              alt="EIF logo"
              className="h-16 w-auto"
            />
          </div>
          <p className="flex-1 text-xs leading-[150%]">{t('eif_disclaimer')}</p>
        </div>
        <hr className="opacity-20 md:col-span-full md:mt-6" />
        <nav className="md:col-span-full md:mt-8">
          <ul className="flex flex-col gap-4 text-center font-medium leading-8 md:flex-row md:items-center md:gap-11">
            <li>{t('all_rights_mydra')}</li>
            <li className="flex items-center gap-2 empty:hidden">
              <LanguageSelector />
              {isLanguageLoading && (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              )}
            </li>
            <li>
              <Link
                target="_blank"
                href={WEBSITE_LINKS.termsAndConditions}
                className="underline"
              >
                {t('terms_of_use')}
              </Link>
            </li>
            <li>
              <Link
                target="_blank"
                href={WEBSITE_LINKS.privacyPolicy}
                className="underline"
              >
                {t('privacy_policy')}
              </Link>
            </li>
            <li>
              <Link
                target="_blank"
                href={EXTERNAL_LINKS.linkedIn}
                className="underline"
              >
                LinkedIn
              </Link>
            </li>
            <li>
              <button
                type="button"
                data-cc="show-preferencesModal"
                className="underline"
              >
                {t('manage_cookie_preferences')}
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </footer>
  )
}
